import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useSettingsStore, type AppSettings } from '@/stores/settingsStore'

interface SettingsContextType {
  settings: AppSettings
  isLoading: boolean
  error: string | null
  updateSetting: <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => Promise<void>
  bulkUpdateSettings: (settings: Partial<AppSettings>) => Promise<void>
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

interface SettingsProviderProps {
  children: ReactNode
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const { 
    settings, 
    isLoading, 
    error, 
    updateSetting, 
    bulkUpdateSettings,
    applyTheme 
  } = useSettingsStore()

  // Settings are fetched by useAppSettings hook in App.tsx
  // No need to fetch here to avoid duplicate calls

  useEffect(() => {
    // Apply theme whenever settings change
    applyTheme(settings.theme)
  }, [settings.theme, applyTheme])

  const contextValue: SettingsContextType = {
    settings,
    isLoading,
    error,
    updateSetting,
    bulkUpdateSettings,
  }

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}
